import type { SessionHistoryItem, SessionStatus } from '@web/api/types'
import { executionTitle } from '@web/lib/executionMode'

export const PENDING_STATUSES = new Set<SessionStatus>(['created', 'starting'])
export const ACTIVE_STATUSES = new Set<SessionStatus>([
  'created',
  'starting',
  'ready',
  'running',
  'requires_action',
  'idle',
])

/** Pending sessions lead every list, newest creation first regardless of updates. */
export function compareSessions(
  left: SessionHistoryItem,
  right: SessionHistoryItem,
): number {
  const leftPending = PENDING_STATUSES.has(left.status)
  const rightPending = PENDING_STATUSES.has(right.status)
  return (
    Number(rightPending) - Number(leftPending) ||
    (leftPending
      ? right.created_at - left.created_at
      : right.updated_at - left.updated_at) ||
    right.created_at - left.created_at ||
    left.id.localeCompare(right.id)
  )
}

/** Keep pending sessions together above active projects, followed by past sessions. */
export function groupSessions(sessions: SessionHistoryItem[], appId?: string) {
  const selected = sessions
    .filter((session) => !appId || session.app_id === appId)
    .sort(compareSessions)
  const pending = selected.filter((session) =>
    PENDING_STATUSES.has(session.status),
  )
  const groups = pending.length
    ? [
        {
          key: 'true:pending',
          active: true,
          project: '',
          items: pending,
          section: 'Active sessions' as string | null,
          showProject: false,
        },
      ]
    : []
  for (const active of [true, false]) {
    const byProject = new Map<string, SessionHistoryItem[]>()
    for (const session of selected) {
      if (
        PENDING_STATUSES.has(session.status) ||
        ACTIVE_STATUSES.has(session.status) !== active
      )
        continue
      const project =
        session.config.execution_mode === 'claude-managed'
          ? `${executionTitle('claude-managed', session.config.provider)} sessions`
          : session.project || 'Blank workspace'
      byProject.set(project, [...(byProject.get(project) ?? []), session])
    }
    groups.push(
      ...[...byProject.entries()]
        .sort((left, right) => compareSessions(left[1][0]!, right[1][0]!))
        .map(([project, items], index) => ({
          key: `${active}:${project}`,
          active,
          project,
          items,
          section:
            index === 0 && !(active && pending.length)
              ? active
                ? 'Active sessions'
                : 'Past sessions'
              : null,
          showProject: byProject.size > 1,
        })),
    )
  }
  return groups
}
